/**
 * useConceptsSearch Hook
 * Fetches concepts from values/search API for Impact Assessment checklist.
 * Maps concept category and search input to API params.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { getConceptsValuesSearch } from '../services/regulationsService';
import type { ConceptGlobalFilterItem } from '../services/regulationsService';
import type {
  ChecklistConceptItem,
  ConceptCategory,
} from '../components/ImpactAssessmentChecklistForm/types';

/** Map UI category to API concept_names (array for values/search) */
const CATEGORY_TO_CONCEPT_NAMES: Record<ConceptCategory, string[] | undefined> = {
  All: undefined,
  'Product Type': ['ProductType'],
  Department: ['Department'],
  'Therapeutic Area': ['TherapeuticArea'],
  Ingredient: ['IngredientName'],
};

/** Map API concept_name to display dimension */
const CONCEPT_NAME_TO_DIMENSION: Record<string, string> = {
  ProductType: 'Product Type',
  Department: 'Department',
  TherapeuticArea: 'Therapeutic Area',
  IngredientName: 'Ingredient',
};

function toChecklistItem(
  item: ConceptGlobalFilterItem,
  dimension: string
): ChecklistConceptItem {
  return {
    id: item.code,
    dimension,
    name: item.name,
    value_api_code: item.code,
    concept_code: item.concept_api_code ?? item.concept_code,
    concept_api_code: item.concept_api_code ?? item.concept_code,
    concept_api_name: item.concept_api_name,
  };
}

interface UseConceptsSearchOptions {
  conceptCategory: ConceptCategory;
  conceptSearch: string;
  pageSize?: number;
  /** When false, skips API calls (e.g. collapsed accordion in process mapping). */
  enabled?: boolean;
}

interface UseConceptsSearchResult {
  concepts: ChecklistConceptItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
}

function mapResponseToItems(
  data: { code: string; name: string; dimension?: string }[],
  conceptCategory: string,
  conceptNames: string[] | undefined
): ChecklistConceptItem[] {
  if (conceptCategory === 'All') {
    return data.map((item) =>
      toChecklistItem(item, item.dimension ?? 'All')
    );
  }
  const dim =
    conceptNames?.[0] && CONCEPT_NAME_TO_DIMENSION[conceptNames[0]]
      ? CONCEPT_NAME_TO_DIMENSION[conceptNames[0]]
      : conceptCategory;
  return data.map((item) => toChecklistItem(item, dim));
}

function getErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Failed to load concepts';
}

/**
 * Hook for fetching concepts from values/search API with infinite scroll.
 * When category is "All", passes empty concept_names (searches all default concepts).
 */
export function useConceptsSearch({
  conceptCategory,
  conceptSearch,
  pageSize = 10,
  enabled = true,
}: UseConceptsSearchOptions): UseConceptsSearchResult {
  const [concepts, setConcepts] = useState<ChecklistConceptItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const requestIdRef = useRef(0);
  const searchKeyRef = useRef('');
  const pageRef = useRef(1);

  const searchName = typeof conceptSearch === 'string' ? conceptSearch.trim() : '';
  const [debouncedSearch, setDebouncedSearch] = useState(searchName);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchName), 300);
    return () => clearTimeout(timer);
  }, [searchName]);

  const conceptNames = CATEGORY_TO_CONCEPT_NAMES[conceptCategory];
  const searchKey = `${conceptCategory}-${debouncedSearch}`;
  searchKeyRef.current = searchKey;

  const fetchPage = useCallback(
    async (pageNum: number, append: boolean) => {
      if (!enabled) return;
      const id = (requestIdRef.current += 1);
      const keyAtStart = searchKeyRef.current;
      if (pageNum === 1) {
        setLoading(true);
        setError(null);
        setConcepts([]);
      } else {
        setLoadingMore(true);
      }
      const isCurrentRequest = () =>
        id === requestIdRef.current && searchKeyRef.current === keyAtStart;

      try {
        const searchParam = debouncedSearch || undefined;
        const res = await getConceptsValuesSearch(
          conceptNames,
          searchParam,
          pageSize,
          pageNum
        );

        if (isCurrentRequest()) {
          const items = mapResponseToItems(
            res.data || [],
            conceptCategory,
            conceptNames
          );
          setHasMore(items.length >= pageSize);
          if (append) {
            setConcepts((prev) => [...prev, ...items]);
            pageRef.current = pageNum;
          } else {
            setConcepts(items);
            pageRef.current = 1;
          }
        }
      } catch (err) {
        if (isCurrentRequest()) {
          setError(getErrorMessage(err));
          if (!append) setConcepts([]);
          setHasMore(false);
        }
      } finally {
        if (id === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [conceptCategory, debouncedSearch, conceptNames, pageSize, enabled]
  );

  useEffect(() => {
    if (!enabled) {
      requestIdRef.current += 1;
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    pageRef.current = 1;
    setHasMore(true);
    fetchPage(1, false);
  }, [fetchPage, enabled]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageRef.current + 1;
    fetchPage(nextPage, true);
  }, [loading, loadingMore, hasMore, fetchPage]);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  const loadMoreStable = useCallback(() => {
    loadMoreRef.current();
  }, []);

  return {
    concepts,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore: loadMoreStable,
  };
}
